import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor, Slide, SlideProps, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getSeverityIcon, getAlertSx, getCloseButtonSx } from 'new/utils/snackbarStyles';

// Types
interface SnackbarMessage {
  id: string;
  message: ReactNode;
  severity: AlertColor;
  autoHideDuration?: number;
}

interface SnackbarContextValue {
  showSuccess: (message: string, duration?: number) => void;
  showSuccessNode: (message: ReactNode, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

// Context
const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

// Slide transition component
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

// Generate unique ID
function generateId(): string {
  return `snackbar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Provider Props
interface SnackbarProviderProps {
  children: ReactNode;
  maxSnackbars?: number;
}

// Provider Component
export function SnackbarProvider({ children, maxSnackbars = 3 }: SnackbarProviderProps) {
  const [snackbars, setSnackbars] = useState<SnackbarMessage[]>([]);

  const addSnackbar = useCallback(
    (message: ReactNode, severity: AlertColor, duration?: number) => {
      const newSnackbar: SnackbarMessage = {
        id: generateId(),
        message,
        severity,
        autoHideDuration: duration,
      };

      setSnackbars((prev) => {
        // Keep only the most recent snackbars
        const updated = [...prev, newSnackbar];
        if (updated.length > maxSnackbars) {
          return updated.slice(-maxSnackbars);
        }
        return updated;
      });
    },
    [maxSnackbars],
  );

  const removeSnackbar = useCallback((id: string) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  }, []);

  const showSuccess = useCallback(
    (message: string, duration = 4000) => {
      addSnackbar(message, 'success', duration);
    },
    [addSnackbar],
  );

  const showSuccessNode = useCallback(
    (message: ReactNode, duration = 4000) => {
      addSnackbar(message, 'success', duration);
    },
    [addSnackbar],
  );

  const showError = useCallback(
    (message: string, duration = 6000) => {
      addSnackbar(message, 'error', duration);
    },
    [addSnackbar],
  );

  const showWarning = useCallback(
    (message: string, duration = 5000) => {
      addSnackbar(message, 'warning', duration);
    },
    [addSnackbar],
  );

  const showInfo = useCallback(
    (message: string, duration = 4000) => {
      addSnackbar(message, 'info', duration);
    },
    [addSnackbar],
  );

  const contextValue = useMemo(
    () => ({
      showSuccess,
      showSuccessNode,
      showError,
      showWarning,
      showInfo,
    }),
    [showSuccess, showSuccessNode, showError, showWarning, showInfo],
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      {snackbars.map((snackbar, index) => (
        <Snackbar
          key={snackbar.id}
          open={true}
          autoHideDuration={snackbar.autoHideDuration}
          onClose={() => removeSnackbar(snackbar.id)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ bottom: { xs: 24 + index * 60, sm: 24 + index * 60 } }}
        >
          <Alert
            severity={snackbar.severity}
            sx={getAlertSx(snackbar.severity)}
            icon={getSeverityIcon(snackbar.severity)}
            onClose={() => removeSnackbar(snackbar.id)}
            action={
              <IconButton
                size="small"
                aria-label="close"
                sx={getCloseButtonSx}
                onClick={() => removeSnackbar(snackbar.id)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  );
}

// Hook
export function useSnackbar(): SnackbarContextValue {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}

export default SnackbarContext;
